// Custom Hook for Study History Tracking
// Tích hợp StudyHistoryService vào components

import { useEffect, useRef, useState } from 'react';
import { studyHistoryService } from '../services/studyHistory/StudyHistoryService';
import { LessonStatsService } from '../services/stats/LessonStatsService';
import { lessonProgressTracker } from '../services/studyHistory/LessonProgressTracker';
import { InteractiveLessonStats } from '../types/learningHistory';
import { useAuth } from '../contexts/AuthContext';

interface UseStudyTrackingOptions {
  lessonId: string;
  lessonTitle: string;
  autoStart?: boolean;
  updateInterval?: number; // milliseconds (default: 60000ms = 60s)
  // Counts for 4 components
  counts?: {
    interactiveQuestions: number;
    videos: number;
    quizzes: number;
    simulations: number;
  };
}

interface StudyTrackingData {
  sessionId: string | null;
  progress: number;
  timeSpent: number;
  isTracking: boolean;
}

export function useStudyTracking(options: UseStudyTrackingOptions) {
  const { user } = useAuth();
  const { 
    lessonId, 
    lessonTitle, 
    autoStart = false, 
    updateInterval = 60000,
    counts = {
      interactiveQuestions: 0,
      videos: 0,
      quizzes: 0,
      simulations: 0
    }
  } = options;
  
  const [trackingData, setTrackingData] = useState<StudyTrackingData>({
    sessionId: null,
    progress: 0,
    timeSpent: 0,
    isTracking: false
  });
  
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartTimeRef = useRef<number>(Date.now()); // For session duration tracking
  const interactiveStartTimeRef = useRef<number>(0); // Track interactive time separately
  const quizStartTimeRef = useRef<number>(0); // Track quiz time separately
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helper: Get interactive stats from LessonStatsService
  const getInteractiveStats = (): InteractiveLessonStats | undefined => {
    const stats = LessonStatsService.getStats(lessonId);
    
    if (!stats || stats.totalQuestions === 0) {
      return undefined; // No interactive questions yet
    }
    
    // Calculate score based on first-attempt correct answers
    const score = stats.totalQuestions > 0 
      ? Math.round((stats.correctAnswers / stats.totalQuestions) * 100)
      : 0;
    
    return {
      correctAnswers: stats.correctAnswers,
      wrongAnswers: stats.wrongAnswers,
      hintsUsed: stats.hintsUsed,
      aiQuestionsAsked: stats.aiQuestionsAsked,
      totalQuestions: stats.totalQuestions,
      score
    };
  };

  // Initialize progress tracker
  useEffect(() => {
    if (user?.id && autoStart) {
      lessonProgressTracker.initializeLesson(lessonId, counts);
      interactiveStartTimeRef.current = Date.now();
      console.log(`📊 [useStudyTracking] Initialized progress tracker with counts:`, counts);
    }
  }, [user?.id, lessonId, autoStart]);

  // Start tracking when component mounts
  useEffect(() => {
    if (!user?.id || !autoStart) return;

    const startTracking = async () => {
      try {
        console.log(`📖 [useStudyTracking] Starting lesson: ${lessonId}`);
        
        // Start lesson in StudyHistoryService
        await studyHistoryService.startLesson(user.id, lessonId, lessonTitle);
        
        // Start study session
        const sessionId = await studyHistoryService.startSession(user.id, 'lesson', lessonId);
        
        sessionIdRef.current = sessionId;
        sessionStartTimeRef.current = Date.now();
        
        setTrackingData(prev => ({
          ...prev,
          sessionId,
          isTracking: true
        }));
        
        console.log(`✅ [useStudyTracking] Tracking started. SessionId: ${sessionId}`);
      } catch (error) {
        console.error('❌ [useStudyTracking] Failed to start tracking:', error);
      }
    };

    startTracking();

    // Cleanup: End session on unmount
    return () => {
      if (sessionIdRef.current) {
        const duration = Math.floor((Date.now() - sessionStartTimeRef.current) / 1000);
        
        studyHistoryService.endSession(sessionIdRef.current, duration).catch(err => {
          console.error('❌ [useStudyTracking] Failed to end session:', err);
        });
        
        console.log(`🛑 [useStudyTracking] Session ended. Duration: ${duration}s`);
      }
    };
  }, [user?.id, lessonId, lessonTitle, autoStart]);

  // Update progress periodically
  useEffect(() => {
    if (!user?.id || !trackingData.isTracking) return;

    progressIntervalRef.current = setInterval(async () => {
      // Get current breakdown from tracker
      const breakdown = lessonProgressTracker.getBreakdown(lessonId);
      
      if (!breakdown) return;

      // Update interactive time (only count active interaction time)
      const interactiveTimeSpent = Math.floor((Date.now() - interactiveStartTimeRef.current) / 1000);
      const stats = LessonStatsService.getStats(lessonId);
      if (stats) {
        lessonProgressTracker.updateInteractive(
          lessonId,
          stats.correctAnswers + stats.wrongAnswers,
          interactiveTimeSpent
        );
      }
      
      setTrackingData(prev => ({
        ...prev,
        progress: breakdown.totalProgress,
        timeSpent: breakdown.totalTimeSpent
      }));

      // Update progress in database
      try {
        const interactiveStats = getInteractiveStats();
        
        await studyHistoryService.updateLessonProgress(
          user.id,
          lessonId,
          breakdown.totalProgress,
          breakdown.totalTimeSpent,
          interactiveStats,
          breakdown
        );
        
        console.log(`📊 [useStudyTracking] Periodic update: ${breakdown.totalProgress.toFixed(1)}%, Time: ${breakdown.totalTimeSpent}s`);
      } catch (error) {
        console.error('❌ [useStudyTracking] Failed to update progress:', error);
      }
    }, updateInterval);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [user?.id, lessonId, trackingData.isTracking, updateInterval]);

  // Update progress manually
  const updateProgress = async (newProgress: number) => {
    if (!user?.id) return;

    // Get progress breakdown from tracker
    const breakdown = lessonProgressTracker.getBreakdown(lessonId);
    const calculatedProgress = breakdown ? breakdown.totalProgress : newProgress;
    const calculatedTimeSpent = breakdown ? breakdown.totalTimeSpent : 0;
    
    setTrackingData(prev => ({
      ...prev,
      progress: calculatedProgress,
      timeSpent: calculatedTimeSpent
    }));

    try {
      const interactiveStats = getInteractiveStats();
      
      await studyHistoryService.updateLessonProgress(
        user.id,
        lessonId,
        calculatedProgress,
        calculatedTimeSpent,
        interactiveStats,
        breakdown || undefined
      );
      
      console.log(`📊 [useStudyTracking] Progress update: ${calculatedProgress.toFixed(1)}% (Time: ${calculatedTimeSpent}s)`);
      if (breakdown) {
        console.log(`  📖 Interactive: ${breakdown.interactive.progress.toFixed(1)}%`);
        console.log(`  📹 Videos: ${breakdown.videos.progress.toFixed(1)}%`);
        console.log(`  ✅ Quiz: ${breakdown.quiz.progress.toFixed(1)}%`);
        console.log(`  🎮 Simulations: ${breakdown.simulations.progress.toFixed(1)}%`);
      }
    } catch (error) {
      console.error('❌ [useStudyTracking] Failed to update progress:', error);
    }
  };

  // Update interactive progress (when user answers questions)
  const updateInteractiveProgress = (questionsAnswered: number) => {
    const interactiveTimeSpent = Math.floor((Date.now() - interactiveStartTimeRef.current) / 1000);
    lessonProgressTracker.updateInteractive(lessonId, questionsAnswered, interactiveTimeSpent);
    
    // Trigger save to database
    const breakdown = lessonProgressTracker.getBreakdown(lessonId);
    if (breakdown) {
      updateProgress(breakdown.totalProgress);
    }
  };

  // Update video progress (from video player)
  const updateVideoProgress = (videosWatched: number, averageWatchPercentage: number) => {
    lessonProgressTracker.updateVideos(lessonId, videosWatched, averageWatchPercentage);
    
    // Trigger save to database
    const breakdown = lessonProgressTracker.getBreakdown(lessonId);
    if (breakdown) {
      updateProgress(breakdown.totalProgress);
    }
  };

  // Update quiz progress (when user completes quiz)
  const updateQuizProgress = (quizzesTaken: number, quizTimeSpent: number) => {
    lessonProgressTracker.updateQuiz(lessonId, quizzesTaken, quizTimeSpent);
    
    // Trigger save to database
    const breakdown = lessonProgressTracker.getBreakdown(lessonId);
    if (breakdown) {
      updateProgress(breakdown.totalProgress);
    }
  };

  // Update simulation progress (from simulation video player)
  const updateSimulationProgress = (simulationsCompleted: number, averageWatchPercentage: number) => {
    lessonProgressTracker.updateSimulations(lessonId, simulationsCompleted, averageWatchPercentage);
    
    // Trigger save to database
    const breakdown = lessonProgressTracker.getBreakdown(lessonId);
    if (breakdown) {
      updateProgress(breakdown.totalProgress);
    }
  };

  // Complete lesson
  const completeLesson = async () => {
    if (!user?.id) return;

    try {
      // Get final breakdown
      const breakdown = lessonProgressTracker.getBreakdown(lessonId);
      const finalProgress = breakdown ? breakdown.totalProgress : 100;
      const finalTimeSpent = breakdown ? breakdown.totalTimeSpent : 0;
      
      // Get final interactive stats
      const interactiveStats = getInteractiveStats();
      
      // Update progress first with final time and stats
      await studyHistoryService.updateLessonProgress(
        user.id,
        lessonId,
        finalProgress,
        finalTimeSpent,
        interactiveStats,
        breakdown || undefined
      );
      
      // Then mark as completed (with stats)
      await studyHistoryService.completeLesson(user.id, lessonId, interactiveStats);
      
      setTrackingData(prev => ({
        ...prev,
        progress: finalProgress,
        timeSpent: finalTimeSpent
      }));
      
      console.log(`🎉 [useStudyTracking] Lesson completed: ${lessonId} (Progress: ${finalProgress.toFixed(1)}%, Time: ${finalTimeSpent}s)`);
      if (interactiveStats) {
        console.log(`📊 Final stats: ${interactiveStats.correctAnswers}/${interactiveStats.totalQuestions} correct (${interactiveStats.score}%)`);
      }
      if (breakdown) {
        console.log(`📊 Final breakdown:`, breakdown);
      }
    } catch (error) {
      console.error('❌ [useStudyTracking] Failed to complete lesson:', error);
    }
  };

  // Add bookmark
  const addBookmark = async (sectionId: string) => {
    if (!user?.id) return;

    try {
      await studyHistoryService.addBookmark(user.id, lessonId, sectionId);
      console.log(`🔖 [useStudyTracking] Bookmark added: ${sectionId}`);
    } catch (error) {
      console.error('❌ [useStudyTracking] Failed to add bookmark:', error);
    }
  };

  // Remove bookmark
  const removeBookmark = async (sectionId: string) => {
    if (!user?.id) return;

    try {
      await studyHistoryService.removeBookmark(user.id, lessonId, sectionId);
      console.log(`🔖 [useStudyTracking] Bookmark removed: ${sectionId}`);
    } catch (error) {
      console.error('❌ [useStudyTracking] Failed to remove bookmark:', error);
    }
  };

  // Update notes
  const updateNotes = async (notes: string) => {
    if (!user?.id) return;

    try {
      await studyHistoryService.updateNotes(user.id, lessonId, notes);
      console.log(`📝 [useStudyTracking] Notes updated`);
    } catch (error) {
      console.error('❌ [useStudyTracking] Failed to update notes:', error);
    }
  };

  return {
    trackingData,
    updateProgress,
    updateInteractiveProgress,
    updateVideoProgress,
    updateQuizProgress,
    updateSimulationProgress,
    completeLesson,
    addBookmark,
    removeBookmark,
    updateNotes
  };
}

